import queryString from "query-string";
import React, { PureComponent } from "react";

import { asyncState, validNumber } from "@utils/calculators";

import {
    CALCULATOR_KEYS,
    trackCalculatorStepStarted,
    trackCalculatorStepSubmitted,
} from "@services/tracking";

import Calculation from "./calculation/calculation.component";
import Result from "./result/result.component";

interface State {
    wallLength: number;
    wallHeight: number;
    brickWidth: number;
    brickLength: number;
    brickHeight: number;
    wallArea: number;
    brickArea: number;
    numberOfBricksHalf: number;
    numberOfBricksFull: number;
    mortarVolumeHalf: number;
    mortarVolumeFull: number;
    jointCementVolume: number;
};

class BrickCalculator extends PureComponent<{}, State> {
    query = queryString.parse(location.search);

    constructor(props: {}) {
        super(props);
        
        this.state = {
            wallLength: this.query.wallLength ? Number(this.query.wallLength) : 0,
            wallHeight: this.query.wallHeight ? Number(this.query.wallHeight) : 0,
            brickWidth: this.query.brickWidth ? Number(this.query.brickWidth) : 0,
            brickLength: this.query.brickLength ? Number(this.query.brickLength) : 0,
            brickHeight: this.query.brickHeight ? Number(this.query.brickHeight) : 0,
            wallArea: 0,
            brickArea: 0,
            numberOfBricksHalf: 0,
            numberOfBricksFull: 0,
            mortarVolumeHalf: 0,
            mortarVolumeFull: 0,
            jointCementVolume: 0,
        };
    }

    async componentDidMount() {
        await this.calculateFields(true);
    }

    // calculations
    wallArea = () => {
        let wallArea = 0;
        // @ts-ignore
        const wallLength = parseFloat(this.state.wallLength);
        // @ts-ignore
        const wallHeight = parseFloat(this.state.wallHeight);

        if (validNumber(wallLength) && validNumber(wallHeight)) {
            wallArea = wallLength * wallHeight;
        }

        return wallArea;
    };
    brickArea = () => {
        let brickArea = 0;
        // @ts-ignore
        const brickLength = parseFloat(this.state.brickLength);
        // @ts-ignore
        const brickHeight = parseFloat(this.state.brickHeight);

        if (validNumber(brickLength) && validNumber(brickHeight)) {
            brickArea = (brickLength + 1) * (brickHeight + 1) * 0.0001;
        }

        return brickArea;
    };
    numberOfBricks = (adjustmentFactor: number) => {
        let numberOfBricks = 0;
        // @ts-ignore
        const wallArea = parseFloat(this.state.wallArea);
        // @ts-ignore
        const brickArea = parseFloat(this.state.brickArea);

        if (validNumber(wallArea) && validNumber(brickArea)) {
            numberOfBricks = (wallArea / brickArea) * adjustmentFactor;
        }

        return numberOfBricks;
    };
    mortarVolumeHalf = () => {
        let mortarVolumeHalf = 0;
        // @ts-ignore
        const brickLength = parseFloat(this.state.brickLength);
        // @ts-ignore
        const brickWidth = parseFloat(this.state.brickWidth);
        // @ts-ignore
        const brickHeight = parseFloat(this.state.brickHeight);

        const brickVolume =
            brickLength * brickWidth * brickHeight * 0.001 * 0.001;
        // @ts-ignore
        const volumeTotalBricks = brickVolume * this.state.numberOfBricksHalf;
        // @ts-ignore
        const wallVolume = this.state.wallArea * brickWidth * 0.01;

        if (validNumber(wallVolume) && validNumber(volumeTotalBricks)) {
            mortarVolumeHalf = wallVolume - volumeTotalBricks;
        }

        return mortarVolumeHalf;
    };
    mortarVolumeFull = () => {
        let mortarVolumeFull = 0;
        const mortarInBetweenDoubleWall = this.wallArea() * 0.01; // wall area x 1 centimeter
        const mortarVolumeHalf = this.mortarVolumeHalf();

        if (
            validNumber(mortarVolumeHalf) &&
            validNumber(mortarInBetweenDoubleWall)
        ) {
            mortarVolumeFull = mortarVolumeHalf * 2 + mortarInBetweenDoubleWall;
        }

        return mortarVolumeFull;
    };
    joinCementVolume = () => {
        const areaJointCement = 0.01 * 0.01;
        // @ts-ignore
        const numberOfBricks = this.state.numberOfBricksHalf;
        // @ts-ignore
        const brickHeight = parseFloat(this.state.brickHeight) / 100;
        // @ts-ignore
        const brickLength = parseFloat(this.state.brickLength) / 100;
        const brickDeadSpace = areaJointCement * 0.01;

        return (
            brickLength * numberOfBricks * areaJointCement +
            brickHeight * numberOfBricks * areaJointCement +
            brickDeadSpace * numberOfBricks
        );
    };

    // setters
    setBrickMeasurements = (length: number, width: number, height: number) => {
        this.setState(
            {
                brickLength: length,
                brickWidth: width,
                brickHeight: height,
            },
            () => {
                // @ts-ignore
                this.calculateFields();
            },
        );
    };
    setCalculationValue = (field: string, value: number) => {
        // @ts-ignore
        this.setState({ [field]: value }, async () => {
            // @ts-ignore
            await this.calculateFields();
        });
    };
    calculateFields = async (initialCall: boolean) => {
        await asyncState(this, {
            wallArea: this.wallArea(),
            brickArea: this.brickArea(),
        });
        await asyncState(this, {
            numberOfBricksHalf: Math.ceil(this.numberOfBricks(1.02)),
            numberOfBricksFull: Math.ceil(this.numberOfBricks(2.04)),
        });
        await asyncState(this, {
            mortarVolumeHalf: this.mortarVolumeHalf(),
            mortarVolumeFull: this.mortarVolumeFull(),
            jointCementVolume: this.joinCementVolume(),
        });

        if (initialCall) {
            trackCalculatorStepStarted(CALCULATOR_KEYS.brick);
        } else {
            trackCalculatorStepSubmitted(CALCULATOR_KEYS.brick);
        }
    };

    render() {
        const calculationProps = {
            setCalculationValue: this.setCalculationValue,
            setBrickMeasurements: this.setBrickMeasurements,
            // @ts-ignore
            wallLength: this.state.wallLength,
            // @ts-ignore
            wallHeight: this.state.wallHeight,
            // @ts-ignore
            brickLength: this.state.brickLength,
            // @ts-ignore
            brickWidth: this.state.brickWidth,
            // @ts-ignore
            brickHeight: this.state.brickHeight,
        };
        const resultsProps = {
            // @ts-ignore
            brickWidth: this.state.brickWidth,
            // @ts-ignore
            brickLength: this.state.brickLength,
            // @ts-ignore
            wallArea: this.state.wallArea,
            // @ts-ignore
            numberOfBricksHalf: this.state.numberOfBricksHalf,
            // @ts-ignore
            numberOfBricksFull: this.state.numberOfBricksFull,
            // @ts-ignore
            mortarVolumeHalf: this.state.mortarVolumeHalf,
            // @ts-ignore
            mortarVolumeFull: this.state.mortarVolumeFull,
            // @ts-ignore
            jointCementVolume: this.state.jointCementVolume,
            mailParams: {
                // @ts-ignore
                wallLength: this.state.wallLength,
                // @ts-ignore
                wallHeight: this.state.wallHeight,
                // @ts-ignore
                brickLength: this.state.brickLength,
                // @ts-ignore
                brickWidth: this.state.brickWidth,
                // @ts-ignore
                brickHeight: this.state.brickHeight,
            },
        };

        return (
            <div id="calculator">
                <Calculation {...calculationProps} />
                <Result {...resultsProps} />
            </div>
        );
    }
}

export { BrickCalculator };
